import { type AiChatState, type AiChatStore } from "./stateConfiguration";
import type { ChatSession } from "@lib/models/ChatSession";
import { type SendMessageResponse } from "@lib/models/SendMessageResponse";
import { ConnectionState } from "@lib/models/connectionState";
import type { AgentContext } from "@lib/models/contexts";
import { type IArtifactService } from "@lib/services/interfaces/artifactService";
import type { AgentGatewayService } from "@lib/services/servers/AgentGatewayService";
import type { ISessionService } from "@lib/services/servers/sessionService/ISessionService";

export const WARNING_THRESHOLD = 0.75;
export const BASE_MULTIPLIER = 1000;
export const IDLE_TIMEOUT = 5 * 60 * 1000; // 10 minutes
export const DEFAULT_WAIT_TIME = 500;
export const MAX_RETRY_WAIT = 20_000;

export interface SessionStateManagerOptions {
    onSessionEstablished?: (sessionId: string) => void;
}

export class SessionStateManagerService {
    private idleTime: number = 0;

    internalHearBeat: ReturnType<typeof setInterval> | null = null;
    lastConCheck: number = 0;
    sessionState: AiChatState;
    agentGatewayService: AgentGatewayService;
    paused: boolean = true;
    waitTimeForRetry: number = DEFAULT_WAIT_TIME;
    sessionService: ISessionService;
    workingContext?: AgentContext;
    artifactService: IArtifactService;
    workingSessionId?: string;
    pendingClosePromise?: Promise<SendMessageResponse | null>;
    private onSessionEstablished?: (sessionId: string) => void;

    constructor(
        agentGatewayService: AgentGatewayService,
        sessionService: ISessionService,
        artifactService: IArtifactService,
        sessionStore: AiChatStore,
        options: SessionStateManagerOptions = {},
    ) {
        this.sessionState = sessionStore.getState();
        this.agentGatewayService = agentGatewayService;
        this.sessionService = sessionService;
        this.artifactService = artifactService;
        this.onSessionEstablished = options.onSessionEstablished;

        sessionStore.subscribe((state, prevState) => {
            this.sessionState = state;
            // The post-turn delayedPatchActiveSession({ interactions }) write used to
            // push the rebuilt interactions array back to /ChatSession/{id}. After
            // the Session collection collapse, interactions are derived client-side
            // from Session.Events (which the server already keeps current), so this
            // write would be redundant — and the target ChatSessions collection is
            // on its way out anyway.
            void prevState;
        });

        this.paused = false;
    }

    /**
     * Update the agent context the manager will use on subsequent reconnects.
     * Called by AiChatProvider when its `agentContext` prop changes — without
     * this, the heartbeat-driven `processRetryLoop` would re-open with the
     * stale context captured at the initial bind.
     */
    setAgentContext(context: AgentContext): void {
        this.workingContext = context;
    }

    pause() {
        this.paused = true;
        if (this.internalHearBeat)
            clearInterval(this.internalHearBeat);
    }

    public start() {
        this.paused = false;
        this.internalHearBeat = setInterval(() => {
            this.beat();
        }, 2000);
    }

    beat() {
        if (this.paused)
            return;

        this.idleTime = new Date().getTime() - this.sessionState.lastActivity;
        if (this.idleTime > IDLE_TIMEOUT) {

            this.sessionState.setIsSessionActive(false);

            if (this.sessionState.connectionStatus == ConnectionState.Connected) {
                this.closeLiveConnection(false)
                    .then(() => this.sessionState.setConnectionStatus(ConnectionState.Disconnected));
            }
        }
        else {
            if (!this.sessionState.sessionActive) {
                this.waitTimeForRetry = DEFAULT_WAIT_TIME;
                this.sessionState.setIsSessionActive(true);
            }

            this.processRetryLoop();
        }
    }

    async createSession(context: AgentContext, sessionId: string): Promise<ChatSession> {
        const session = await this.sessionService.createSession(sessionId, context?.agentConfig.name ?? '', context?.customer);

        if (!session?.id)
            throw new Error(`SessionStateManagerService.createSession: sessionService returned no id for ${sessionId}`);

        session.interactions.forEach((i) => {
            i.turn_complete = true;
            if (i.artifactData) {
                i.artifactData.forEach((a) => this.artifactService.addToCache(a));
            }
        });

        return session;
    }

    async retrieveSession(_context: AgentContext, sessionId: string): Promise<ChatSession | null> {
        const session = await this.sessionService.retrieveSession(sessionId);

        if (!session?.id)
            return null;

        session.interactions.forEach((i) => {
            i.turn_complete = true;
            if (i.artifactData) {
                i.artifactData.forEach((a) => this.artifactService.addToCache(a));
            }
        });

        return session;
    }

    async bindAndOpenLiveSession(context: AgentContext, sessionId?: string) {
        this.pause();

        if (this.sessionState.connectionStatus != ConnectionState.Disconnected)
            await this.stop();

        if (this.pendingClosePromise) {
            await this.pendingClosePromise;
            this.pendingClosePromise = undefined;
        }

        let session = this.sessionState.session;

        const loadingType = sessionId ? 'EXISTING' : 'NEW';
        this.sessionState.setLoadingInformation(ConnectionState.Connecting, false, loadingType);

        try {
            this.workingContext = context;
            this.workingSessionId = sessionId;

            if (sessionId && sessionId != this.sessionState.session?.id) {
                session = null;

                this.sessionState.setLoadingInformation(ConnectionState.Connecting, true, loadingType);
                try {
                    session = await this.retrieveSession(context, sessionId);

                    this.onSessionEstablished?.(sessionId);

                    this.workingSessionId = sessionId;
                    this.agentGatewayService.setSession(sessionId, '');
                }
                finally {
                    this.sessionState.setLoadingInformation(ConnectionState.Connecting, false, loadingType);
                }
            }

            if (!sessionId)
                session = null;

            this.sessionState.setLoadingInformation(ConnectionState.Connecting, false, loadingType, session?.loadError);

            const socketSessionId = await this.agentGatewayService.openConnection(this.workingSessionId);

            if (!session || session?.id !== socketSessionId) {

                session = {
                    fullyLoaded: true,
                    id: socketSessionId,
                    activityCount:0,
                    artifacts:[],
                    browserScopeId:'',
                    interactions:[],
                    user: session?.user!,
                    overview: '',
                    sessionRunMode: 'interactive',
                    agentType: 'Unknown',
                    created: new Date()
                }

                if (sessionId && sessionId !== socketSessionId)
                    console.warn(`Session Id: ${sessionId} was not found a new one was created: ${socketSessionId}`);

                this.onSessionEstablished?.(socketSessionId);

                this.workingSessionId = session.id;
                this.sessionState.setSession(session);
                this.agentGatewayService.setSession(socketSessionId, '');
            }

            if (sessionId != this.sessionState.session?.id)
                this.sessionState.setSession(session);

            this.sessionState.setLoadingInformation(ConnectionState.Connected, false, undefined, undefined);
        }
        catch (error: any) {
            console.error("StateManager:  Open Error ", sessionId, error);
            this.sessionState.setLoadingInformation(ConnectionState.Disconnected, false, undefined);
            throw error;
        }
        finally {
            this.start();
        }
    }

    async closeLiveConnection(stoneCold: boolean = false, closeTurn: boolean = true) {

        this.waitTimeForRetry = DEFAULT_WAIT_TIME;

        if (this.sessionState.connectionStatus != ConnectionState.Connected)
            throw new Error("SessionStateManagerService.closeLiveConnection: cannot close a disconnected session");

        this.pause();
        try {
            await this.agentGatewayService.closeConnection(closeTurn);
            this.sessionState.setConnectionStatus(ConnectionState.Disconnected);
        }
        finally {
            if (!stoneCold)
                this.start();
        }
    }

    async stop() {
        if (this.internalHearBeat)
            clearInterval(this.internalHearBeat);
        this.internalHearBeat = null;
        if (this.sessionState.connectionStatus === ConnectionState.Connected) {
            await this.closeLiveConnection(true);
        }
    }

    processRetryLoop(_identification?: string) {
        const now = new Date().getTime();

        if (!this.workingContext)
            return;

        if (this.sessionState.sessionActive && (this.sessionState.connectionStatus == ConnectionState.Disconnected)) {
            const diff = now - this.lastConCheck;

            if (diff >= this.waitTimeForRetry) {
                // Exponential backoff: w_n+1 = min(w_n + w_n^2 / BASE, MAX). Pulled
                // out of the previous `Math.min(this.waitTimeForRetry = ...)` form
                // which mixed an assignment expression into the comparand.
                const nextWait = this.waitTimeForRetry + (this.waitTimeForRetry * this.waitTimeForRetry / BASE_MULTIPLIER);
                this.waitTimeForRetry = Math.min(nextWait, MAX_RETRY_WAIT);
                this.lastConCheck = now;

                this.bindAndOpenLiveSession(this.workingContext, this.workingSessionId)
                    .catch((error) => console.error("Error bind and open", error));
            }
        }
        else {
            this.waitTimeForRetry = DEFAULT_WAIT_TIME;
        }
    }
}
